import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getFormattedDateTime } from "@/utils";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { OrderApi } from "@/api/order.api";
import { Skeleton } from "@/components/ui/skeleton";
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";

const getStatusBadge = (status: string) => {
  const label = status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/(^|\s)\S/g, (l) => l.toUpperCase());
  let color = "bg-gray-100 text-gray-800";
  if (status === "FINALIZED") color = "bg-green-100 text-green-800";
  else if (status === "CREATED") color = "bg-blue-100 text-blue-800";
  else if (status === "CANCELLED") color = "bg-red-100 text-red-800";
  return (
    <span
      className={`inline-block px-2 py-1 rounded text-xs font-medium ${color}`}
    >
      {label}
    </span>
  );
};

function formatDateTime(ms) {
  if (!ms) return "-";
  return getFormattedDateTime(Number(ms)) || "-";
}

function formatAmount(amount: number | string | undefined) {
  if (amount === undefined || amount === null || isNaN(Number(amount)))
    return "-";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));
}

export default function OrderDetails() {
  const [tab, setTab] = useState("order");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderState, setOrderState] = useState<any>(null);

  // Get orderId from URL params
  const orderId = window.location.pathname.split("/").pop();

  async function fetchOrder() {
    try {
      setLoading(true);
      setError(null);
      const { data } = await OrderApi.fetchOrder(orderId);
      setOrderState(data);
    } catch (err) {
      setError(err?.message || "Failed to fetch order details");
      toast.error(err?.message || "Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchOrder();
    if (orderState?.orderStatus) setNewStatus(orderState.orderStatus);
    // eslint-disable-next-line
  }, [orderId]);

  async function handleUpdateStatus() {
    setLoading(true);
    try {
      const { data } = await OrderApi.updateOrderStatus({
        id: orderState.id,
        orderStatus: newStatus,
      });
      setOrderState(data);
      toast.success("Order status updated successfully");
      setSheetOpen(false);
    } catch (e) {
      toast.error(e?.message || "Failed to update order status");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="mb-6">
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-32 w-full mb-4" />
        </div>
      </div>
    );
  }
  if (error) {
    return <div className="p-8 max-w-6xl mx-auto text-red-500">{error}</div>;
  }
  if (!orderState) {
    return (
      <div className="p-8 max-w-6xl mx-auto text-gray-500">No order found.</div>
    );
  }

  const order = orderState;
  const item =
    order.lineItems && order.lineItems.length > 0
      ? order.lineItems[0]
      : { giftCardProductVO: {} };
  const product = item.giftCardProductVO || {};
  const ORDER_STATUSES = [
    "CREATED",
    "FINALIZED",
    "SHIPPED",
    "DELIVERED",
    "CANCELLED",
  ];
  const REASONS = [
    "Customer Request",
    "Payment Failed",
    "Manual Correction",
    "System Error",
    "Other",
  ];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/rewardpoint/orders">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Order Details</h1>
        </div>
        <PermissionGuard permissionCode={PERMISSION_CODES.UPDATE_ORDER}>
          <Button onClick={() => setSheetOpen(true)} variant="outline">
            Update Status
          </Button>
        </PermissionGuard>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="mb-4">
          <TabsTrigger value="order">Order Details</TabsTrigger>
          <TabsTrigger value="product">Product Details</TabsTrigger>
        </TabsList>
        <TabsContent value="order">
          <Card className="p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2 ">
              <div>
                <div className="text-xs text-gray-500 mb-1">Program</div>
                <div className="text-md mb-1">
                  {order.programVO?.programName || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Member Id</div>
                <div className="text-md mb-1">
                  {order.programMemberId || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Order Id</div>
                <div className="text-md mb-1">{order.id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Product Code</div>
                <div className="text-md mb-1">
                  {(product as any)?.productCode ?? "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Product Name</div>
                <div className="text-md mb-1">
                  {(product as any)?.productName ?? "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Merchant</div>
                <div className="text-md mb-1">
                  {(product as any)?.redemptionMerchantVO?.merchantName ?? "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Brand</div>
                <div className="text-md mb-1">
                  {(product as any)?.brandVO?.brandName ?? "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Order Date</div>
                <div className="text-md mb-1">
                  {formatDateTime(order.createdDate)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Status</div>
                <div className="text-md mb-1">
                  {getStatusBadge(order.orderStatus)}
                </div>
              </div>
            </div>
          </Card>
          {/* Program Charge */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">Program Charge</div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Program Currency
                </div>
                <div className="text-md mb-1">
                  {order.currencyVO?.iso3CurrencyCode || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Points</div>
                <div className="text-md mb-1">{formatAmount(order.points)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPPL</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Change Charge</div>
                <div className="text-md mb-1">0</div>
              </div>
            </div>
          </Card>
          {/* Merchant Charge */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">Merchant Charge</div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Merchant Currency
                </div>
                <div className="text-md mb-1">
                  {(product as any)?.currencyVO?.iso3CurrencyCode ?? "AED"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Sourcing Cost</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Settlement</div>
                <div className="text-md mb-1">0</div>
              </div>
            </div>
          </Card>
          {/* CPP Margin */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">CPP Margin</div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">Members Paid</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Members Currency
                </div>
                <div className="text-md mb-1">
                  {order.currencyVO?.iso3CurrencyCode || "INR"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPPM</div>
                <div className="text-md mb-1">0.01</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Points Paid</div>
                <div className="text-md mb-1">
                  {formatAmount((item as any)?.points)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPPL</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Charge</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPP Margin</div>
                <div className="text-md mb-1">0</div>
              </div>
            </div>
          </Card>
          {/* Loyyal Profit */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">Loyyal Profit</div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">LYL Currency</div>
                <div className="text-md mb-1">USD</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Charge</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Settlement</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Card Expense</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Profit</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">PRD Margin</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPP Margin</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Check</div>
                <div className="text-md mb-1">-</div>
              </div>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="product">
          <Card className="p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
              <div>
                <div className="text-xs text-gray-500 mb-1">Item Price</div>
                <div className="text-md mb-1">
                  {formatAmount((item as any)?.cashAmount)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">No. of Items</div>
                <div className="text-md mb-1">
                  {typeof (item as any)?.orderItemQuantity === "number"
                    ? (item as any).orderItemQuantity
                    : 1}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Total Amounts</div>
                <div className="text-md mb-1">
                  {formatAmount((item as any)?.cashAmount)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPPM</div>
                <div className="text-md mb-1">0.01</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Total Points Price
                </div>
                <div className="text-md mb-1">
                  {formatAmount((item as any)?.cashAmount)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Membership Currency
                </div>
                <div className="text-md mb-1">
                  {order.currencyVO?.iso3CurrencyCode || "INR"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Cash Paid</div>
                <div className="text-md mb-1">
                  {formatAmount((item as any)?.cashAmount)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Points Paid</div>
                <div className="text-md mb-1">
                  {formatAmount((item as any)?.points)}
                </div>
              </div>
            </div>
          </Card>
          {/* Repeat the same charge/profit cards as in Order Details tab */}
          {/* Program Charge */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">Program Charge</div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Program Currency
                </div>
                <div className="text-md mb-1">
                  {order.currencyVO?.iso3CurrencyCode || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Points</div>
                <div className="text-md mb-1">{formatAmount(order.points)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPPL</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Change Charge</div>
                <div className="text-md mb-1">0</div>
              </div>
            </div>
          </Card>
          {/* Merchant Charge */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">Merchant Charge</div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Merchant Currency
                </div>
                <div className="text-md mb-1">
                  {(product as any)?.currencyVO?.iso3CurrencyCode ?? "AED"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Sourcing Cost</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Settlement</div>
                <div className="text-md mb-1">0</div>
              </div>
            </div>
          </Card>
          {/* CPP Margin */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">CPP Margin</div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">Members Paid</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Members Currency
                </div>
                <div className="text-md mb-1">
                  {order.currencyVO?.iso3CurrencyCode || "INR"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPPM</div>
                <div className="text-md mb-1">0.01</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Points Paid</div>
                <div className="text-md mb-1">
                  {formatAmount((item as any)?.points)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPPL</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Charge</div>
                <div className="text-md mb-1">0</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPP Margin</div>
                <div className="text-md mb-1">0</div>
              </div>
            </div>
          </Card>
          {/* Loyyal Profit */}
          <Card className="p-4 mb-4 bg-gray-50">
            <div className="font-semibold mb-2">Loyyal Profit</div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">LYL Currency</div>
                <div className="text-md mb-1">USD</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Charge</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Settlement</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Card Expense</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Profit</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">PRD Margin</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">CPP Margin</div>
                <div className="text-md mb-1">-</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Check</div>
                <div className="text-md mb-1">-</div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Update Order Status</SheetTitle>
            <p className="text-sm text-gray-500 mt-1">
              Update the order status and provide any relevant comments or
              references. All changes are logged for audit purposes.
            </p>
          </SheetHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Order Status
              </label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {getStatusBadge(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Reason for Status Change
              </label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Comments / Reference
              </label>
              <Input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add comments or reference..."
              />
            </div>
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="confirm"
                checked={confirmed}
                onCheckedChange={(val) => setConfirmed(val === true)}
              />
              <label htmlFor="confirm" className="text-sm">
                I confirm this change is correct
              </label>
            </div>
          </div>
          <SheetFooter className="mt-6">
            <SheetClose asChild>
              <Button type="button" variant="outline" disabled={loading}>
                Cancel
              </Button>
            </SheetClose>
            <Button
              type="button"
              onClick={handleUpdateStatus}
              disabled={!confirmed || loading}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
